import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Badge, Button, Alert } from 'react-bootstrap';
import { CheckCircle, XCircle, Clock } from 'react-bootstrap-icons';
import moment from 'moment';
import { toast } from 'react-toastify';
import { shiftAPI } from '../../services';
import { getUserFromLocalStorage } from '../../utils/auth';

const shiftLabel = (shift) => (shift.is_training ? 'ca training' : 'ca tăng cường');

const OvertimeApprovalTab = ({ isActive, withBranch }) => {
  const [loading, setLoading] = useState(false);
  const [overtimeShifts, setOvertimeShifts] = useState([]);
  const [actionShiftId, setActionShiftId] = useState(null);

  const { dateFrom, dateTo } = useMemo(() => {
    const to = moment().endOf('day');
    const from = moment().subtract(2, 'month').startOf('day');
    return {
      dateFrom: from.format('YYYY-MM-DD'),
      dateTo: to.format('YYYY-MM-DD'),
    };
  }, []);

  const loadOvertimeShifts = useCallback(async () => {
    if (!withBranch) return;
    setLoading(true);
    try {
      const response = await shiftAPI.getShifts({
        date_from: dateFrom,
        date_to: dateTo,
        page: 1,
        limit: 500,
        status: 'COMPLETED_WAIT_APPROVE',
      });

      const data = response?.data || response || [];

      const filtered = (Array.isArray(data) ? data : data.data || [])
        .filter(
          (shift) => shift.is_overtime && (shift.status === 'IN_PROGRESS' || shift.status === 'COMPLETED_WAIT_APPROVE'),
        )
        .sort((a, b) => moment(b.start_time).valueOf() - moment(a.start_time).valueOf());

      setOvertimeShifts(filtered);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading overtime shifts:', error);
      toast.error('Lỗi khi tải danh sách ca tăng cường');
    } finally {
      setLoading(false);
    }
  }, [withBranch, dateFrom, dateTo]);

  useEffect(() => {
    if (!isActive) return;
    loadOvertimeShifts();
  }, [isActive, loadOvertimeShifts]);

  const handleApproveOvertime = async (shiftId) => {
    const user = getUserFromLocalStorage();
    if (!user?.id) {
      toast.error('Không tìm thấy thông tin người dùng');
      return;
    }
    const shift = overtimeShifts.find((s) => s.id === shiftId);
    const label = shiftLabel(shift || {});
    try {
      setActionShiftId(shiftId);
      await shiftAPI.approveOvertime(shiftId, user.id);
      toast.success(`Đã duyệt ${label}`);
      await loadOvertimeShifts();
    } catch (error) {
      toast.error(error?.message || `Duyệt ${label} thất bại`);
    } finally {
      setActionShiftId(null);
    }
  };

  const handleRejectOvertime = async (shiftId) => {
    const user = getUserFromLocalStorage();
    if (!user?.id) {
      toast.error('Không tìm thấy thông tin người dùng');
      return;
    }
    const shift = overtimeShifts.find((s) => s.id === shiftId);
    const label = shiftLabel(shift || {});
    const reason = window.prompt(`Nhập lý do từ chối ${label} (tuỳ chọn):`) || undefined;

    try {
      setActionShiftId(shiftId);
      await shiftAPI.rejectOvertime(shiftId, user.id, reason);
      toast.success(`Đã từ chối ${label}`);
      await loadOvertimeShifts();
    } catch (error) {
      toast.error(error?.message || `Từ chối ${label} thất bại`);
    } finally {
      setActionShiftId(null);
    }
  };

  if (!isActive) {
    return null;
  }

  return (
    <Card>
      <Card.Body>
        <Row className="align-items-center mb-4">
          <Col>
            <h4 className="mb-0 d-flex align-items-center gap-2">
              <Clock size={18} />
              Duyệt ca tăng cường / Training
            </h4>
            <div className="text-muted small mt-1">
              Hiển thị ca tăng cường và ca training cần duyệt trong vòng 2 tháng gần nhất ({moment(dateFrom).format('DD/MM/YYYY')}{' '}
              - {moment(dateTo).format('DD/MM/YYYY')}).
            </div>
          </Col>
          <Col xs="auto">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={loadOvertimeShifts}
              disabled={loading}
              className="d-flex align-items-center gap-2"
            >
              Làm mới
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5 text-muted">Đang tải...</div>
        ) : overtimeShifts.length === 0 ? (
          <Alert variant="info">Không có ca tăng cường / training nào cần duyệt.</Alert>
        ) : (
          <div className="d-flex flex-column gap-3">
            {overtimeShifts.map((shift) => {
              const dateLabel = moment(shift.start_time).format('dddd, DD/MM/YYYY');
              const timeLabel = `${moment(shift.start_time).format(
                'HH:mm',
              )} - ${moment(shift.end_time).format('HH:mm')}`;
              const isCompletedWaiting = shift.status === 'COMPLETED_WAIT_APPROVE';

              return (
                <Card
                  key={shift.id}
                  className={isCompletedWaiting ? 'border-success' : 'border-warning'}
                >
                  <Card.Body>
                    <Row className="align-items-center">
                      <Col>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <h5 className="mb-0">{shift.employee_name || 'Nhân viên không xác định'}</h5>
                          {shift.is_training
                            ? <Badge bg="primary">📚 Ca training</Badge>
                            : <Badge bg="info">Ca tăng cường</Badge>}
                        </div>
                        <div className="text-muted small mb-1">{dateLabel}</div>
                        <div className="text-muted mb-2">{timeLabel}</div>
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg={isCompletedWaiting ? 'success' : 'primary'}>{shift.status}</Badge>
                        </div>
                      </Col>
                      <Col xs="auto">
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => handleApproveOvertime(shift.id)}
                            disabled={actionShiftId === shift.id}
                            className="d-flex align-items-center gap-1"
                          >
                            <CheckCircle size={16} />
                            Duyệt
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleRejectOvertime(shift.id)}
                            disabled={actionShiftId === shift.id}
                            className="d-flex align-items-center gap-1"
                          >
                            <XCircle size={16} />
                            Từ chối
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default OvertimeApprovalTab;
